import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2, Save, RefreshCw, Download } from 'lucide-react'
import { adminClient, type AppConfig, type RaynetSource, type RaynetCategory } from '@/api/adminClient'
// import { toast } from 'sonner'

// Dočasné jednoduché toast řešení
const toast = {
  success: (title: string, opts?: { description?: string }) => {
    alert(`✅ ${title}${opts?.description ? '\n' + opts.description : ''}`)
  },
  error: (title: string, opts?: { description?: string }) => {
    alert(`❌ ${title}${opts?.description ? '\n' + opts.description : ''}`)
  }
}

export default function ConfigurationTab() {
  const [, setConfig] = useState<AppConfig>({})
  const [sources, setSources] = useState<RaynetSource[]>([])
  const [categories, setCategories] = useState<RaynetCategory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setSaving] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isSyncingCategories, setIsSyncingCategories] = useState(false)
  
  // Form state
  const [leadTopic, setLeadTopic] = useState('')
  const [selectedSource, setSelectedSource] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  useEffect(() => {
    loadData(true) // Při prvním načtení automaticky synchronizujeme zdroje
  }, [])

  const loadData = async (autoSync = false) => {
    setIsLoading(true)
    try {
      // Pokud je autoSync zapnutý, nejdříve synchronizujeme zdroje a kategorie z Raynet
      if (autoSync) {
        try {
          await Promise.all([
            adminClient.syncSources(),
            adminClient.syncCategories()
          ])
          console.log('✅ Automatická synchronizace zdrojů a kategorií dokončena')
        } catch (syncError) {
          console.warn('⚠️ Automatická synchronizace selhala, používáme lokální cache:', syncError)
        }
      }
      
      const [configData, sourcesData, categoriesData] = await Promise.all([
        adminClient.getConfig(),
        adminClient.getSources(),
        adminClient.getCategories()
      ])
      
      setConfig(configData)
      setSources(sourcesData)
      setCategories(categoriesData)
      
      // Nastavíme form hodnoty
      setLeadTopic(configData.lead_topic || '')
      setSelectedSource(configData.lead_source || '')
      setSelectedCategory(configData.lead_category || '')
      
    } catch (error) {
      toast.error('Chyba při načítání konfigurace', {
        description: error instanceof Error ? error.message : 'Neznámá chyba'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await Promise.all([
        adminClient.setConfig('lead_topic', leadTopic),
        adminClient.setConfig('lead_source', selectedSource),
        adminClient.setConfig('lead_category', selectedCategory)
      ])
      
      toast.success('Konfigurace byla uložena', {
        description: 'Změny jsou nyní aktivní'
      })
      
      // Znovu načteme data
      await loadData(false)
      
    } catch (error) {
      toast.error('Chyba při ukládání konfigurace', {
        description: error instanceof Error ? error.message : 'Neznámá chyba'
      })
    } finally {
      setSaving(false)
    }
  }

  const handleSyncSources = async () => {
    setIsSyncing(true)
    try {
      const result = await adminClient.syncSources()
      
      toast.success('Synchronizace zdrojů dokončena', {
        description: result.message
      })
      
      // Znovu načteme data
      await loadData(false)
      
    } catch (error) {
      toast.error('Chyba při synchronizaci zdrojů', {
        description: error instanceof Error ? error.message : 'Neznámá chyba'
      })
    } finally {
      setIsSyncing(false)
    }
  }

  const handleSyncCategories = async () => {
    setIsSyncingCategories(true)
    try {
      const result = await adminClient.syncCategories()
      
      toast.success('Synchronizace kategorií dokončena', {
        description: result.message
      })
      
      // Znovu načteme data
      await loadData(false)
      
    } catch (error) {
      toast.error('Chyba při synchronizaci kategorií', {
        description: error instanceof Error ? error.message : 'Neznámá chyba'
      })
    } finally {
      setIsSyncingCategories(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Nastavení leadů</CardTitle>
          <CardDescription>
            Konfigurace parametrů pro vytváření leadů v Raynet CRM
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="leadTopic">Předmět leadu</Label>
            <Input
              id="leadTopic"
              value={leadTopic}
              onChange={(e) => setLeadTopic(e.target.value)}
              placeholder="Zadejte předmět, který se bude používat pro nové leady"
            />
            <p className="text-sm text-muted-foreground">
              Tento text se použije jako předmět při vytváření leadu v Raynetu
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="leadSource">Zdroj leadu</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncSources}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-2" />
                    Synchronizuji...
                  </>
                ) : (
                  <>
                    <Download className="h-3 w-3 mr-2" />
                    Načíst z Raynet
                  </>
                )}
              </Button>
            </div>
            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger>
                <SelectValue placeholder="Vyberte zdroj leadu" />
              </SelectTrigger>
              <SelectContent>
                {sources.map((source) => (
                  <SelectItem key={source.id} value={source.id.toString()}>
                    {source.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Zdroj bude automaticky přiřazen všem leadům z kiosku. Tlačítko "Načíst z Raynet" synchronizuje aktuální zdroje z CRM.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="leadCategory">Kategorie leadu</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncCategories}
                disabled={isSyncingCategories}
              >
                {isSyncingCategories ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-2" />
                    Synchronizuji...
                  </>
                ) : (
                  <>
                    <Download className="h-3 w-3 mr-2" />
                    Načíst z Raynet
                  </>
                )}
              </Button>
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Vyberte kategorii leadu" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id.toString()}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Kategorie pomáhá třídit leady podle jejich důležitosti. Tlačítko "Načíst z Raynet" synchronizuje aktuální kategorie z CRM.
            </p>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Ukládám...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Uložit změny
                </>
              )}
            </Button>
            
            <Button variant="outline" onClick={() => loadData(false)} disabled={isLoading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Obnovit
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Systémové informace</CardTitle>
          <CardDescription>
            Aktuální konfigurace systému
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">Raynet instance</h4>
              <p className="text-sm text-muted-foreground">
                {import.meta.env.VITE_RAYNET_INSTANCE || 'Není nakonfigurováno'}
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Raynet uživatel</h4>
              <p className="text-sm text-muted-foreground">
                {import.meta.env.VITE_RAYNET_USER || 'Není nakonfigurováno'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 